import { useEffect, useRef, useState } from "react";
import api from "../utils/api/axiosInstance";
import { capMatchPercent } from "../utils/matchScoreUtils";
import { getMatchExplanationBody } from "../utils/fetchProductMatchExplanation";
import { hasMatchIntent, resolveMatchIntent } from "../utils/resolveMatchIntent";
import { useUserAuth } from "../contexts/UserAuthContext";
import useHomeChatMatchSession from "./useHomeChatMatchSession";

const DEFAULT_BATCH_SIZE = 30;

/**
 * Batches match-score lookups for a grid/list of product cards into a single
 * POST /products/match-explanations/batch request instead of firing one
 * match-explanation request per card (which previously caused N+1 API calls
 * on category, search, and similar-products pages).
 */
export default function useProductGridMatchScores({
  products = [],
  enabled = true,
  maxProducts = DEFAULT_BATCH_SIZE,
}) {
  const { user } = useUserAuth();
  const aiSession = useHomeChatMatchSession(user, { syncServer: false });
  const [scoresById, setScoresById] = useState({});
  const [ready, setReady] = useState(false);

  const batch = products.slice(0, maxProducts);
  const productKey = batch
    .filter((p) => !p?.aiRanked)
    .map((p) => p._id || p.id)
    .filter(Boolean)
    .join(",");

  // Keep the latest values in refs so the fetch effect can depend on stable,
  // content-derived keys instead of object references that change identity
  // on every render.
  const latestRef = useRef();
  latestRef.current = { aiSession, user };

  useEffect(() => {
    const { aiSession: currentAiSession, user: currentUser } =
      latestRef.current;
    const ids = productKey ? productKey.split(",") : [];

    if (!enabled || ids.length === 0) {
      setScoresById({});
      setReady(true);
      return undefined;
    }

    const guestIntent = resolveMatchIntent({
      aiSession: currentAiSession,
      user: null,
    });
    if (!currentUser && !hasMatchIntent(guestIntent.wizardAnswers)) {
      setScoresById({});
      setReady(true);
      return undefined;
    }

    const controller = new AbortController();
    setReady(false);

    const body = {
      productIds: ids,
      ...getMatchExplanationBody(currentUser, { aiSession: currentAiSession }),
    };

    api
      .post("/products/match-explanations/batch", body, {
        signal: controller.signal,
      })
      .then(({ data }) => {
        const results = data?.results || {};
        const next = {};
        Object.entries(results).forEach(([productId, payload]) => {
          if (!payload || payload.error) return;
          const overallScore = payload.match?.overallScore;
          if (overallScore === null || overallScore === undefined) return;
          const lowConfidence =
            payload.scoring?.source === "low_confidence" ||
            (payload.scoring?.confidence?.dataPoints ?? 0) < 2;
          next[productId] = {
            score: capMatchPercent(overallScore),
            lowConfidence,
          };
        });
        setScoresById(next);
      })
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setScoresById({});
      })
      .finally(() => {
        setReady(true);
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, productKey]);

  // Shape consumed directly by useProductMatchScore's `precomputed` option.
  const getMatchScore = (productId) => {
    if (!productId) return { enabled: false };
    const resolved = scoresById[String(productId)];
    return {
      enabled: true,
      ready,
      score: resolved?.score ?? null,
      lowConfidence: resolved?.lowConfidence || false,
    };
  };

  return { scoresById, getMatchScore, ready };
}
